import { Query } from '@skedulo/protobuf-web'
import { DataSchema } from '@skedulo/protobuf-web/dist/query/api'
import {
  ComparatorType,
  DropdownOption,
  IComparatorOption,
  IFilterOption,
} from '../types'
import {
  appendNullComparatorOptions,
  getComparatorOptions,
  getComparatorOptionsFromAcceptableFilters,
} from './comparator-utils'

/**
 * Returns the list of comparator options for a given QueryAPI field value type.
 * For example, Enum, String, DateTime.  This is used to determine what dropdown
 * filters are available in the Filter modal, such as IN, NOT_IN, STARTS_WITH, etc.
 *
 * `includeNullComparators` opts a caller in to IS_NULL / IS_NOT_NULL for nullable fields.
 * It defaults to off because only the EQL query builder can build a real query from them today -
 * the Lucene builder's node/request-filter conversion has no null-comparison mapping yet.
 */
export const getFieldComparatorOptions = (
  field: IFilterOption,
  relational: boolean,
  includeNullComparators = false,
): IComparatorOption[] => {
  // acceptableFilters is the backend's declared contract for what this field supports -
  // it never includes null comparisons, so null comparators are never appended here,
  // regardless of includeNullComparators.
  if (field.acceptableFilters && field.acceptableFilters.length > 0) {
    return getComparatorOptionsFromAcceptableFilters(
      field.kind,
      field.acceptableFilters,
      relational,
    )
  }

  const options = getComparatorOptions(
    field.kind,
    field.isList,
    relational,
  ).filter((comp) => comp !== undefined)

  return includeNullComparators
    ? appendNullComparatorOptions(options, field)
    : options
}

export interface IFieldDefinition {
  type: Query.API.DataField_Kind
  isList: boolean
  comparatorOptions: IComparatorOption[]
  valueOptions?: DropdownOption[]
}

/**
 * Checks if the current comparator is in the list of supported comparators for a given field
 */
export const isValidComparator = (
  fieldDefinition: IFieldDefinition | null,
  comparator: ComparatorType,
) =>
  fieldDefinition?.comparatorOptions.find(({ value }) => value === comparator)

export const getFieldDefinition = (
  selectedField?: IFilterOption,
  dataSchema?: DataSchema,
  includeNullComparators = false,
) => {
  if (selectedField && dataSchema) {
    let valueOptions: DropdownOption[] | undefined

    if (selectedField.kind === Query.API.DataField_Kind.BOOL) {
      valueOptions = [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ]
    }

    if (selectedField.kind === Query.API.DataField_Kind.ENUM) {
      valueOptions = selectedField?.enum?.values?.map(
        ({ stringValue = '', label = '' }) => ({
          value: stringValue,
          label,
        }),
      )
    }

    const fieldRelationship = dataSchema?.relationships?.find(
      (r) => r?.localField?.fieldName === selectedField?.value,
    )

    const comparatorOptions = getFieldComparatorOptions(
      selectedField,
      !!fieldRelationship,
      includeNullComparators,
    )

    return {
      type: selectedField.kind,
      isList: selectedField.isList,
      comparatorOptions,
      valueOptions,
      fieldRelationship,
    }
  }
  return null
}
